import { HttpClient, HttpContext, HttpEvent, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, finalize, map, of, shareReplay } from 'rxjs';
import { Param } from './param';
import { ConfigurationService } from '../configuration/configuration.service';
import { HIDE_LOADER } from 'src/app/shared/loader/loading.interceptor';
import { ApiResponse } from './response/apiResponse';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private ongoingRequestsCache: Map<string, Observable<HttpEvent<any>>> = new Map();

  private sendHeaderTimescaleEnabled = false;

  constructor(
    private httpClient: HttpClient,
    private configurationService: ConfigurationService,
  ) {}

  public get<U>(route: string, params?: Param[], hideLoader?: boolean): Observable<HttpEvent<U>> {
    const cacheKey = this.createCacheKey(route, params);

    // Check if there's already a request in progress with the same parameters
    if (this.ongoingRequestsCache.has(cacheKey)) {
      return this.ongoingRequestsCache.get(cacheKey) as Observable<HttpEvent<U>>;
    }

    let headers = new HttpHeaders();

    const request$ = this.httpClient
      .get<U>(this.configurationService.config.BASE_API + route + this.paramParser(params), {
        context: new HttpContext().set(HIDE_LOADER, !!hideLoader),
        observe: 'response',
        headers,
      })
      .pipe(
        shareReplay(1), // Share the response among all subscribers
        finalize(() => this.ongoingRequestsCache.delete(cacheKey)), // Remove from cache once completed
      );

    // Store the request in the cache
    this.ongoingRequestsCache.set(cacheKey, request$);
    return request$;
  }

  private createCacheKey(route: string, params?: Param[]): string {
    return route + JSON.stringify(params);
  }

  public getApiResponseBody<U>(route: string, params?: Param[], hideLoader?: boolean) {
    const obs = this.get<ApiResponse<U>>(route, params, hideLoader);
    return obs
      .pipe(
        catchError((error) => {
          console.error('An error occurred while requesting :' + route, error);
          return of(null);
        }),
      )
      .pipe(
        map((data) => {
          if (data instanceof HttpResponse) {
            return data.body?.body!;
          }
          throw new Error('Unexpected response type from ' + route);
        }),
      );
  }

  public getBlob(route: string, hideLoader?: boolean) {
    let headers = new HttpHeaders();
    if (this.sendHeaderTimescaleEnabled) {
      headers = headers.set('x-timescale-enabled', '1');
    }

    return this.httpClient.get(this.configurationService.config.BASE_API + route, {
      context: new HttpContext().set(HIDE_LOADER, !!hideLoader),
      observe: 'response',
      responseType: 'blob',
      headers,
    });
  }

  public post<U>(route: string, body?: any): Observable<HttpEvent<U>> {
    return this.httpClient.post<U>(this.configurationService.config.BASE_API + route, body, {
      observe: 'response',
    });
  }

  public postAndGetApiResponseBody<U>(route: string, body?: any) {
    const obs = this.post<ApiResponse<U>>(route, body);
    return obs.pipe(
      map((data) => {
        if (data instanceof HttpResponse) {
          return data.body?.body!;
        }
        console.log('Unexpected response type data', data);
        throw new Error('Unexpected response type from ' + route);
      }),
    );
  }

  public put<U>(route: string, body?: any): Observable<HttpEvent<U>> {
    return this.httpClient.put<U>(this.configurationService.config.BASE_API + route, body, {
      observe: 'response',
    });
  }

  public putAndGetApiResponseBody<U>(route: string, body?: any) {
    const obs = this.put<ApiResponse<U>>(route, body);
    return obs.pipe(
      map((data) => {
        if (data instanceof HttpResponse) {
          return data.body?.body!;
        }
        console.log('Unexpected response type data', data);
        throw new Error('Unexpected response type from ' + route);
      }),
    );
  }
  public delete(route: string) {
    return this.httpClient.delete(this.configurationService.config.BASE_API + route);
  }

  public postWithResponse<U>(route: string, params?: FormData, headers?: HttpHeaders): Observable<HttpResponse<U>> {
    return this.httpClient.post<U>(this.configurationService.config.BASE_API + route, params, {
      observe: 'response',
      headers: headers,
    });
  }

  public uploadFile<U>(route: string, formData: FormData): Observable<HttpEvent<U>> {
    return this.httpClient.post<U>(this.configurationService.config.BASE_API + route, formData, {
      observe: 'response',
    });
  }

  public uploadFileAndGetApiResponseBody<U>(route: string, formData: FormData) {
    const obs = this.uploadFile<ApiResponse<U>>(route, formData);
    return obs.pipe(
      map((data) => {
        if (data instanceof HttpResponse) {
          return data.body?.body!;
        }
        console.log('Unexpected response type data', data);
        throw new Error('Unexpected response type from ' + route);
      }),
    );
  }

  private paramParser(params?: Param[]): string {
    if (!params) {
      return '';
    }

    let stringParams = '?';

    for (const param of params) {
      if (stringParams !== '?') {
        stringParams += '&';
      }
      stringParams += param.serialize();
    }

    if (stringParams === '?') {
      return '';
    }
    return stringParams;
  }
}
